/**
 * Costs Tab - Business Value Calculator
 * Sprint 7: Day 2 COMPLETE
 *
 * Features:
 * - Sticky cost summary banner with 10 cost categories
 * - Accordion sections for all cost categories
 * - Real-time auto-calculation on ANY input change
 * - Personnel, Technology, 6 Line Item sections, Risk, Duration, Financial Parameters
 */

import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSection } from './Accordion';
import { PersonnelSection, PersonnelRole, DEFAULT_PERSONNEL_ROLES } from './PersonnelSection';
import { TechnologySection, TechnologyCost, DEFAULT_TECHNOLOGY_COSTS } from './TechnologySection';
import { LineItemSection, LineItem, LINE_ITEM_CATEGORIES, createDefaultLineItem } from './LineItemSection';
import { RiskSection, RiskBuffer, DEFAULT_RISK_BUFFER } from './RiskSection';
import { DurationSection, ProjectDuration, DEFAULT_PROJECT_DURATION } from './DurationSection';
import { FinancialParametersSection, FinancialParameters, DEFAULT_FINANCIAL_PARAMETERS } from './FinancialParametersSection';

interface CostsTabProps {
  onCostsUpdate?: (totalCost: number) => void;
}

export const CostsTab: React.FC<CostsTabProps> = ({ onCostsUpdate }) => {
  // State management - Personnel & Technology
  const [personnelRoles, setPersonnelRoles] = useState<PersonnelRole[]>(DEFAULT_PERSONNEL_ROLES);
  const [technologyCosts, setTechnologyCosts] = useState<TechnologyCost>(DEFAULT_TECHNOLOGY_COSTS);

  // State management - Line Item sections
  const [trainingItems, setTrainingItems] = useState<LineItem[]>([createDefaultLineItem()]);
  const [migrationItems, setMigrationItems] = useState<LineItem[]>([createDefaultLineItem()]);
  const [complianceItems, setComplianceItems] = useState<LineItem[]>([createDefaultLineItem()]);
  const [vendorItems, setVendorItems] = useState<LineItem[]>([createDefaultLineItem()]);
  const [disruptionItems, setDisruptionItems] = useState<LineItem[]>([createDefaultLineItem()]);

  // State management - Risk, Duration, Financial Parameters
  const [riskBuffer, setRiskBuffer] = useState<RiskBuffer>(DEFAULT_RISK_BUFFER);
  const [duration, setDuration] = useState<ProjectDuration>(DEFAULT_PROJECT_DURATION);
  const [financialParams, setFinancialParams] = useState<FinancialParameters>(DEFAULT_FINANCIAL_PARAMETERS);

  // ==================== COST CALCULATIONS ====================

  // Personnel total
  const personnelTotal = personnelRoles.reduce((sum, role) => sum + role.totalCost, 0);

  // Technology total
  const capexTotal = technologyCosts.capex.servers + technologyCosts.capex.storage + technologyCosts.capex.licenses;
  const opexMonthly = technologyCosts.opex.cloudHosting + technologyCosts.opex.saasSubscriptions;
  const opexTotal = opexMonthly * 36;
  const technologyTotal = capexTotal + opexTotal;

  // Line item totals
  const trainingTotal = trainingItems.reduce((sum, item) => sum + item.totalCost, 0);
  const migrationTotal = migrationItems.reduce((sum, item) => sum + item.totalCost, 0);
  const complianceTotal = complianceItems.reduce((sum, item) => sum + item.totalCost, 0);
  const vendorTotal = vendorItems.reduce((sum, item) => sum + item.totalCost, 0);
  const disruptionTotal = disruptionItems.reduce((sum, item) => sum + item.totalCost, 0);

  // Other costs total (for risk calculation)
  const otherCostsTotal = trainingTotal + migrationTotal + complianceTotal + vendorTotal + disruptionTotal;

  // Grand total (base costs + risk buffer)
  const grandTotalBase = personnelTotal + technologyTotal + otherCostsTotal;
  const grandTotal = grandTotalBase + riskBuffer.totalBuffer;

  // Auto-calculation: notify parent when total changes
  useEffect(() => {
    if (onCostsUpdate) {
      onCostsUpdate(grandTotal);
    }
  }, [grandTotal, onCostsUpdate]);

  // Auto-recalculate risk buffer when base costs change
  useEffect(() => {
    const updatedRiskBuffer: RiskBuffer = {
      ...riskBuffer,
      personnelBuffer: (personnelTotal * riskBuffer.personnelRiskPct) / 100,
      technologyBuffer: (technologyTotal * riskBuffer.technologyRiskPct) / 100,
      otherCostsBuffer: (otherCostsTotal * riskBuffer.otherCostsRiskPct) / 100,
      totalBuffer: 0 // Will be calculated below
    };

    updatedRiskBuffer.totalBuffer =
      updatedRiskBuffer.personnelBuffer +
      updatedRiskBuffer.technologyBuffer +
      updatedRiskBuffer.otherCostsBuffer;

    setRiskBuffer(updatedRiskBuffer);
  }, [personnelTotal, technologyTotal, otherCostsTotal, riskBuffer, riskBuffer.personnelRiskPct, riskBuffer.technologyRiskPct, riskBuffer.otherCostsRiskPct]);

  // ==================== EVENT HANDLERS ====================

  // Personnel
  const handlePersonnelUpdate = (roleId: string, field: keyof PersonnelRole, value: number) => {
    setPersonnelRoles((prevRoles) =>
      prevRoles.map((role) => {
        if (role.id !== roleId) return role;

        const updatedRole = { ...role, [field]: value };

        // Recalculate total cost: FTE × (Annual Salary / 12) × Duration
        if (field === 'fte' || field === 'annualSalary' || field === 'durationMonths') {
          updatedRole.totalCost = updatedRole.fte * (updatedRole.annualSalary / 12) * updatedRole.durationMonths;
        }

        return updatedRole;
      })
    );
  };

  // Technology
  const handleTechnologyUpdate = (category: 'capex' | 'opex', field: string, value: number) => {
    setTechnologyCosts((prevCosts) => ({
      ...prevCosts,
      [category]: {
        ...prevCosts[category],
        [field]: value
      }
    }));
  };

  // Line Items - Generic handler
  const createLineItemHandlers = (
    items: LineItem[],
    setItems: React.Dispatch<React.SetStateAction<LineItem[]>>
  ) => ({
    onItemUpdate: (itemId: string, field: keyof LineItem, value: string | number) => {
      setItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id !== itemId) return item;

          const updatedItem = { ...item, [field]: value };

          // Recalculate total: Unit Cost × Quantity
          if (field === 'unitCost' || field === 'quantity') {
            updatedItem.totalCost = updatedItem.unitCost * updatedItem.quantity;
          }

          return updatedItem;
        })
      );
    },
    onItemAdd: () => {
      setItems((prevItems) => [...prevItems, createDefaultLineItem()]);
    },
    onItemRemove: (itemId: string) => {
      setItems((prevItems) => {
        if (prevItems.length === 1) return prevItems; // Keep at least 1 item
        return prevItems.filter((item) => item.id !== itemId);
      });
    }
  });

  const trainingHandlers = createLineItemHandlers(trainingItems, setTrainingItems);
  const migrationHandlers = createLineItemHandlers(migrationItems, setMigrationItems);
  const complianceHandlers = createLineItemHandlers(complianceItems, setComplianceItems);
  const vendorHandlers = createLineItemHandlers(vendorItems, setVendorItems);
  const disruptionHandlers = createLineItemHandlers(disruptionItems, setDisruptionItems);

  // Risk Buffer
  const handleRiskUpdate = (field: keyof RiskBuffer, value: number) => {
    setRiskBuffer((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Duration
  const handleDurationUpdate = (field: keyof ProjectDuration, value: number) => {
    setDuration((prev) => ({
      ...prev,
      [field]: value,
      totalMonths: field === 'totalMonths' ? value : prev.planningMonths + prev.implementationMonths + prev.optimizationMonths
    }));
  };

  // Financial Parameters
  const handleFinancialParamsUpdate = (field: keyof FinancialParameters, value: number) => {
    setFinancialParams((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // ==================== ACCORDION SECTIONS ====================

  const accordionSections: AccordionSection[] = [
    {
      id: 'personnel',
      title: 'Personnel',
      subtitle: '8 roles with FTE, salary, and duration',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      value: personnelTotal,
      children: (
        <PersonnelSection
          roles={personnelRoles}
          onRoleUpdate={handlePersonnelUpdate}
        />
      )
    },
    {
      id: 'technology',
      title: 'Technology',
      subtitle: 'CapEx (one-time) and OpEx (monthly × 36)',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      value: technologyTotal,
      children: (
        <TechnologySection
          costs={technologyCosts}
          onCostUpdate={handleTechnologyUpdate}
        />
      )
    },
    {
      id: 'training',
      title: 'Training',
      subtitle: 'User training, documentation, e-learning',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      ),
      value: trainingTotal,
      children: (
        <LineItemSection
          sectionName="Training"
          items={trainingItems}
          categories={LINE_ITEM_CATEGORIES.training}
          helpText="Costs for training materials, instructor-led sessions, e-learning platforms, and user documentation."
          {...trainingHandlers}
        />
      )
    },
    {
      id: 'migration',
      title: 'Migration',
      subtitle: 'Data extraction, ETL, validation',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      ),
      value: migrationTotal,
      children: (
        <LineItemSection
          sectionName="Migration"
          items={migrationItems}
          categories={LINE_ITEM_CATEGORIES.migration}
          helpText="Costs for data extraction, ETL development, schema mapping, and validation testing."
          {...migrationHandlers}
        />
      )
    },
    {
      id: 'compliance',
      title: 'Compliance',
      subtitle: 'Regulatory audit, certifications',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      value: complianceTotal,
      children: (
        <LineItemSection
          sectionName="Compliance"
          items={complianceItems}
          categories={LINE_ITEM_CATEGORIES.compliance}
          helpText="Costs for regulatory audits, policy documentation, GDPR assessment, and compliance certifications."
          {...complianceHandlers}
        />
      )
    },
    {
      id: 'vendor',
      title: 'Vendor',
      subtitle: 'Third-party licenses, consulting',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      value: vendorTotal,
      children: (
        <LineItemSection
          sectionName="Vendor"
          items={vendorItems}
          categories={LINE_ITEM_CATEGORIES.vendor}
          helpText="Costs for third-party software licenses, professional services, support contracts, and consulting."
          {...vendorHandlers}
        />
      )
    },
    {
      id: 'disruption',
      title: 'Disruption',
      subtitle: 'Downtime, change management',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      value: disruptionTotal,
      children: (
        <LineItemSection
          sectionName="Disruption"
          items={disruptionItems}
          categories={LINE_ITEM_CATEGORIES.disruption}
          helpText="Costs for downtime planning, communication campaigns, change management, and productivity loss."
          {...disruptionHandlers}
        />
      )
    },
    {
      id: 'risk',
      title: 'Risk Buffer',
      subtitle: 'Contingency reserves (% of base costs)',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
      value: riskBuffer.totalBuffer,
      children: (
        <RiskSection
          personnelTotal={personnelTotal}
          technologyTotal={technologyTotal}
          otherCostsTotal={otherCostsTotal}
          riskBuffer={riskBuffer}
          onRiskUpdate={handleRiskUpdate}
        />
      )
    },
    {
      id: 'duration',
      title: 'Project Duration',
      subtitle: '12-month allocation across phases',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      value: undefined,
      children: (
        <DurationSection
          duration={duration}
          onDurationUpdate={handleDurationUpdate}
        />
      )
    },
    {
      id: 'financial',
      title: 'Financial Parameters',
      subtitle: 'Discount, tax, inflation rates',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      value: undefined,
      children: (
        <FinancialParametersSection
          parameters={financialParams}
          onParameterUpdate={handleFinancialParamsUpdate}
        />
      )
    }
  ];

  // ==================== RENDER ====================

  return (
    <div className="costs-tab">
      {/* Cost Summary Banner (sticky) */}
      <div className="cost-summary-banner">
        <div className="cost-summary-content">
          <div className="cost-summary-main">
            <div className="cost-summary-label">Total Project Cost</div>
            <div className="cost-summary-value">${grandTotal.toLocaleString()}</div>
            <div className="cost-summary-breakdown-inline">
              <span>${grandTotalBase.toLocaleString()} base</span>
              <span>+</span>
              <span>${riskBuffer.totalBuffer.toLocaleString()} risk buffer</span>
            </div>
          </div>
          <div className="cost-summary-breakdown">
            <div className="cost-summary-item">
              <div className="cost-summary-item-label">Personnel</div>
              <div className="cost-summary-item-value">${personnelTotal.toLocaleString()}</div>
            </div>
            <div className="cost-summary-item">
              <div className="cost-summary-item-label">Technology</div>
              <div className="cost-summary-item-value">${technologyTotal.toLocaleString()}</div>
            </div>
            <div className="cost-summary-item">
              <div className="cost-summary-item-label">Training</div>
              <div className="cost-summary-item-value">${trainingTotal.toLocaleString()}</div>
            </div>
            <div className="cost-summary-item">
              <div className="cost-summary-item-label">Migration</div>
              <div className="cost-summary-item-value">${migrationTotal.toLocaleString()}</div>
            </div>
            <div className="cost-summary-item">
              <div className="cost-summary-item-label">Compliance</div>
              <div className="cost-summary-item-value">${complianceTotal.toLocaleString()}</div>
            </div>
            <div className="cost-summary-item">
              <div className="cost-summary-item-label">Vendor</div>
              <div className="cost-summary-item-value">${vendorTotal.toLocaleString()}</div>
            </div>
            <div className="cost-summary-item">
              <div className="cost-summary-item-label">Disruption</div>
              <div className="cost-summary-item-value">${disruptionTotal.toLocaleString()}</div>
            </div>
            <div className="cost-summary-item cost-summary-item-risk">
              <div className="cost-summary-item-label">Risk Buffer</div>
              <div className="cost-summary-item-value">${riskBuffer.totalBuffer.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion Group */}
      <Accordion sections={accordionSections} defaultOpenId="personnel" />

      <style>{`
        .costs-tab {
          padding: 2rem 0;
        }

        /* Cost Summary Banner (sticky at top) */
        .cost-summary-banner {
          position: sticky;
          top: 0;
          z-index: 100;
          background: linear-gradient(135deg, #0094ab 0%, #004772 100%);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 8px 32px rgba(0, 148, 171, 0.3);
        }

        .cost-summary-content {
          display: flex;
          align-items: center;
          gap: 3rem;
        }

        .cost-summary-main {
          flex-shrink: 0;
        }

        .cost-summary-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.5rem;
        }

        .cost-summary-value {
          font-size: 3rem;
          font-weight: 800;
          color: #ffffff;
          line-height: 1;
        }

        .cost-summary-breakdown-inline {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .cost-summary-breakdown {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
          gap: 1.5rem;
          flex: 1;
        }

        .cost-summary-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .cost-summary-item-label {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.7);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .cost-summary-item-value {
          font-size: 1.125rem;
          font-weight: 700;
          color: #ffffff;
        }

        .cost-summary-item-risk {
          border-left: 2px solid rgba(255, 255, 255, 0.3);
          padding-left: 1rem;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .cost-summary-content {
            flex-direction: column;
            gap: 1.5rem;
          }

          .cost-summary-value {
            font-size: 2rem;
          }

          .cost-summary-breakdown {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 640px) {
          .cost-summary-value {
            font-size: 1.5rem;
          }

          .cost-summary-breakdown {
            grid-template-columns: repeat(2, 1fr);
          }

          .cost-summary-item-value {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CostsTab;
